import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { identitaFiscale } from '../lib/identitaFiscale';
import { Search, Plus } from 'lucide-react';
import { listClienti } from '../api/clienti';
import type { ClienteList } from '../types/cliente';
import { useAuthStore } from '../store/authStore';
import { SkeletonRow } from '../components/ui/Skeleton';
import { canWriteAnagraficaCommerciale } from '../lib/constants';
import { useDebounce } from '../hooks/useDebounce';

function Pagination({ page, pages, onPage }: { page: number; pages: number; onPage: (p: number) => void }) {
  if (pages <= 1) return null;
  const nums: (number | '...')[] = [];
  if (pages <= 7) {
    for (let i = 1; i <= pages; i++) nums.push(i);
  } else {
    nums.push(1, 2);
    if (page > 4) nums.push('...');
    for (let i = Math.max(3, page - 1); i <= Math.min(pages - 2, page + 1); i++) nums.push(i);
    if (page < pages - 3) nums.push('...');
    nums.push(pages - 1, pages);
  }
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <button type="button" onClick={() => onPage(page - 1)} disabled={page === 1}
        className="px-3 py-1.5 text-sm border border-stone-border rounded-md text-ink-mid hover:bg-stone-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
        ‹
      </button>
      {nums.map((n, i) =>
        n === '...' ? (
          <span key={`ellipsis-${i}`} className="px-2 text-ink-faint text-sm">…</span>
        ) : (
          <button key={n} type="button" onClick={() => onPage(n as number)}
            className={`px-3 py-1.5 text-sm border rounded-md transition-colors ${
              n === page ? 'bg-sage text-white border-sage font-semibold' : 'border-stone-border text-ink-mid hover:bg-stone-bg'
            }`}>
            {n}
          </button>
        )
      )}
      <button type="button" onClick={() => onPage(page + 1)} disabled={page === pages}
        className="px-3 py-1.5 text-sm border border-stone-border rounded-md text-ink-mid hover:bg-stone-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
        ›
      </button>
    </div>
  );
}

export default function ClientiPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const userRole = user?.role ?? '';
  const canEdit = canWriteAnagraficaCommerciale(userRole);

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<ClienteList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reset pagina al cambio ricerca: adjust-during-render (idioma React,
  // "you might not need an effect") invece di un effect con setState.
  const [prevSearch, setPrevSearch] = useState(debouncedSearch);
  if (prevSearch !== debouncedSearch) {
    setPrevSearch(debouncedSearch);
    setPage(1);
  }

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        setResult(await listClienti({ page, limit: 50, search: debouncedSearch || undefined }));
      } catch {
        setError(t('anagrafiche.clienti.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [page, debouncedSearch, t]);

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-ink-dark tracking-tight">{t('anagrafiche.clienti.titolo')}</h1>
            {result && (
              <p className="text-sm text-ink-soft mt-0.5">
                {result.total === 1 ? t('anagrafiche.clienti.conteggio_uno', { n: result.total }) : t('anagrafiche.clienti.conteggio_molti', { n: result.total })}
              </p>
            )}
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={() => navigate('/clienti/nuovo')}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors"
            >
              <Plus size={15} />
              {t('anagrafiche.clienti.nuovo')}
            </button>
          )}
        </div>

        <div className="relative mb-4 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('anagrafiche.clienti.cerca_placeholder')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-border rounded-lg focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20"
          />
        </div>

        <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-mid bg-stone-bg">
                <th className="text-left px-5 py-3 text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('anagrafiche.campi.ragione_sociale')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('anagrafiche.clienti.col_piva_cf')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('anagrafiche.campi.referente')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('anagrafiche.campi.telefono')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('anagrafiche.clienti.col_ordini')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-mid">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <td key={j} className="px-5 py-3"><SkeletonRow /></td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-accent-danger text-sm">{error}</td>
                </tr>
              ) : !result || result.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-ink-soft text-sm italic">
                    {debouncedSearch ? t('anagrafiche.comune.nessun_risultato') : t('anagrafiche.clienti.vuoto')}
                  </td>
                </tr>
              ) : (
                result.items.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/clienti/${c.id}`)}
                    className="hover:bg-stone-bg transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-3 font-medium text-ink-dark">{c.ragione_sociale}</td>
                    <td className="px-5 py-3 text-ink-soft font-mono text-xs">
                      {identitaFiscale(c) || '—'}
                    </td>
                    <td className="px-5 py-3 text-ink-soft">{c.referente ?? '—'}</td>
                    <td className="px-5 py-3 text-ink-soft">{c.telefono ?? '—'}</td>
                    <td className="px-5 py-3 text-ink-soft">{c.n_ordini ?? 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {result && result.pages > 1 && (
            <div className="px-5 py-4 border-t border-stone-mid flex items-center justify-between">
              <p className="text-xs text-ink-soft">
                {t('anagrafiche.comune.pagina', { page: result.page, pages: result.pages })}
              </p>
              <Pagination page={result.page} pages={result.pages} onPage={setPage} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
